import * as React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { createSource, updateSource, getOracleEpmConfigBySource, getCubesBySource, createOracleEpmConfig } from '../../../../slices/source';
import { OracleEPMConnectionStep } from './OracleEPMConnectionStep';
import { OracleEPMConfigurationStep } from './OracleEPMConfigurationStep';
import { OracleEPMCubesStep } from './OracleEPMCubesStep';

export const OracleEPMWizard = ({
  mode,
  originId,
  source,
  hostname,
  username,
  password,
  isAdminCredential,
  version,
  applicationName,
  metadataExportName,
  retryInterval,
  maxRetries,
  loadingEpmData,
  onHostnameChange,
  onUsernameChange,
  onPasswordChange,
  onAdminCredentialChange,
  onVersionChange,
  onApplicationNameChange,
  onMetadataExportNameChange,
  onRetryIntervalChange,
  onMaxRetriesChange,
  connectionTest,
  syncMetadata,
  cubeManagement,
  onClose
}) => {
  const dispatch = useDispatch();
  const [wizardStep, setWizardStep] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [configLoading, setConfigLoading] = React.useState(false);
  const [skipLoading, setSkipLoading] = React.useState(false);
  const [createdSourceId, setCreatedSourceId] = React.useState(mode === 'Edit' ? originId : null);
  const [loadedEpmConfig, setLoadedEpmConfig] = React.useState(null);

  const wizardSteps = ['Setup and Test Connection', 'Oracle EPM Configuration', 'Setup Cubes'];

  // Step validation functions
  const isStep1Valid = () => {
    return source && hostname && username && password && connectionTest.testResult && connectionTest.testResult.is_connected;
  };

  const isStep2Valid = () => {
    return createdSourceId && version && applicationName && metadataExportName && retryInterval && maxRetries && syncMetadata.syncResult && syncMetadata.syncResult.success;
  };

  const isStep3Valid = () => {
    return cubeManagement.cubes.length > 0 && cubeManagement.cubes.every(cube => cube.name && cube.dimensions.length > 0);
  };

  const handleBack = () => {
    if (wizardStep > 0) {
      setWizardStep(wizardStep - 1);
    }
  };

  const handleSkip = async () => {
    if (mode === 'Edit' && originId) {
      setSkipLoading(true);
      try {
        if (wizardStep === 0) {
          const epmConfigResult = await dispatch(getOracleEpmConfigBySource(originId)).unwrap();
          if (epmConfigResult) {
            setLoadedEpmConfig(epmConfigResult);
            onVersionChange({ target: { value: epmConfigResult.version || "" }});
            onApplicationNameChange({ target: { value: epmConfigResult.application_name || "" }});
            onMetadataExportNameChange({ target: { value: epmConfigResult.metadata_export_name || "" }});
            onRetryIntervalChange({ target: { value: epmConfigResult.retry_interval?.toString() || "" }});
            onMaxRetriesChange({ target: { value: epmConfigResult.max_retries?.toString() || "" }});
          }
        } else if (wizardStep === 1) {
          const cubesResult = await dispatch(getCubesBySource({ sourceId: originId })).unwrap();
          if (cubesResult && cubesResult.items) {
            cubeManagement.setLoadedCubes(cubesResult.items);
            const formattedCubes = cubesResult.items.map(cube => ({
              id: cube.id,
              name: cube.cube_name,
              dimensions: cube.dimensions ? cube.dimensions.map(dim => dim.dimension || dim.dimension_name || 'Unknown Dimension') : []
            }));
            cubeManagement.setCubes(formattedCubes);

            const allDimensions = new Set();
            cubesResult.items.forEach(cube => {
              if (cube.dimensions) {
                cube.dimensions.forEach(dim => {
                  const dimensionName = dim.dimension || dim.dimension_name || 'Unknown Dimension';
                  allDimensions.add(dimensionName);
                });
              }
            });
            syncMetadata.setAvailableDimensions(Array.from(allDimensions));
          }
        }
      } catch (error) {
        console.error('Error downloading data for skip:', error);
      } finally {
        setSkipLoading(false);
      }
    }

    if (wizardStep < wizardSteps.length - 1) {
      setWizardStep(wizardStep + 1);
    }
  };

  const handleSaveEPMDBAndNext = async () => {
    if (!source || !hostname || !username || !password) {
      window.alert('Please fill in all required fields: Source Name, Hostname, Username, and Password');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', source);
      formData.append('type', 'OracleEPM');
      formData.append('hostname', hostname);
      formData.append('username', username);
      formData.append('password', password);
      formData.append('is_admin_credential', isAdminCredential);
      formData.append('database', '');
      formData.append('port', '');

      const action = mode === "Edit" ? updateSource({ sourceId: originId, sourceInfo: formData }) : createSource(formData);
      const result = await dispatch(action).unwrap();
      
      const sourceId = mode === "Edit" ? originId : result.id;
      setCreatedSourceId(sourceId);
      
      const message = mode === "Edit" ? 'Oracle EPM source updated successfully!' : 'Oracle EPM source saved successfully!';
      window.alert(message);
      
      if (wizardStep < wizardSteps.length - 1) {
        setWizardStep(wizardStep + 1);
      }
    } catch (error) {
      console.error('Error saving Oracle EPM source:', error);
      const message = mode === "Edit" ? 'Failed to update Oracle EPM source. Please try again.' : 'Failed to save Oracle EPM source. Please try again.';
      window.alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEPMConfigurationAndNext = async () => {
    if (!createdSourceId || !version || !applicationName || !metadataExportName || !retryInterval || !maxRetries) {
      window.alert('Please fill in all required fields: Version, Application Name, Metadata Export Name, Retry Interval, and Max Retries');
      return;
    }

    setConfigLoading(true);
    try {
      const formData = new FormData();
      formData.append('source_id', createdSourceId);
      formData.append('version', version);
      formData.append('application_name', applicationName);
      formData.append('metadata_export_name', metadataExportName);
      formData.append('retry_interval', retryInterval);
      formData.append('max_retries', maxRetries);

      await dispatch(createOracleEpmConfig(formData)).unwrap();
      
      try {
        console.log('Loading cubes for sourceId:', createdSourceId);
        const cubesResult = await dispatch(getCubesBySource({ sourceId: createdSourceId })).unwrap();
        
        if (cubesResult && cubesResult.items && cubesResult.items.length > 0) {
          console.log('Loaded', cubesResult.items.length, 'cubes from database');
          
          cubeManagement.setLoadedCubes(cubesResult.items);
          
          const formattedCubes = cubesResult.items.map(cube => ({
            id: cube.id,
            name: cube.cube_name,
            dimensions: cube.dimensions ? cube.dimensions.map(dim => dim.dimension || dim.dimension_name || 'Unknown Dimension') : []
          }));
          
          cubeManagement.setCubes(formattedCubes);

          const allDimensions = new Set();
          cubesResult.items.forEach(cube => {
            if (cube.dimensions) {
              cube.dimensions.forEach(dim => {
                const dimensionName = dim.dimension || dim.dimension_name || 'Unknown Dimension';
                allDimensions.add(dimensionName);
              });
            }
          });
          
          if (syncMetadata.availableDimensions && syncMetadata.availableDimensions.length > 0) {
            syncMetadata.availableDimensions.forEach(dim => allDimensions.add(dim));
          }
          
          console.log('Available dimensions (merged from cubes and sync):', Array.from(allDimensions));
          syncMetadata.setAvailableDimensions(Array.from(allDimensions));
        } else {
          console.log('No cubes found in database, preserving dimensions from sync');
          cubeManagement.setCubes([]);
          cubeManagement.setLoadedCubes([]);
        }
      } catch (cubeError) {
        console.error('Error loading cube data:', cubeError);
        cubeManagement.setCubes([]);
        cubeManagement.setLoadedCubes([]);
      }
      
      window.alert('Oracle EPM configuration saved successfully!');
      
      setTimeout(() => {
        if (wizardStep < wizardSteps.length - 1) {
          setWizardStep(wizardStep + 1);
        }
      }, 500);
      
    } catch (error) {
      console.error('Error saving Oracle EPM configuration:', error);
      window.alert('Failed to save Oracle EPM configuration. Please try again.');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveCubes = async () => {
    await cubeManagement.handleSaveCubes(createdSourceId, onClose);
  };

  return (
    <Box sx={{ maxWidth: '100%', width: '100%', mt: 2 }}>
      {loadingEpmData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading Oracle EPM configuration and cube data...
          </Typography>
        </Box>
      )}
      <Stepper activeStep={wizardStep} orientation="vertical">
        <Step>
          <StepLabel>Setup and Test Connection</StepLabel>
          <StepContent>
            <OracleEPMConnectionStep
              mode={mode}
              hostname={hostname}
              username={username}
              password={password}
              isAdminCredential={isAdminCredential}
              testLoading={connectionTest.testLoading}
              testResult={connectionTest.testResult}
              loading={loading}
              skipLoading={skipLoading}
              isValid={isStep1Valid()}
              onHostnameChange={onHostnameChange}
              onUsernameChange={onUsernameChange}
              onPasswordChange={onPasswordChange}
              onAdminCredentialChange={onAdminCredentialChange}
              onTestConnection={() => connectionTest.handleTestConnection('OracleEPM', hostname, username, password, '', '')}
              onSaveAndNext={handleSaveEPMDBAndNext}
              onSkip={handleSkip}
            />
          </StepContent>
        </Step>
        
        <Step>
          <StepLabel>Oracle EPM Configuration</StepLabel>
          <StepContent>
            <OracleEPMConfigurationStep
              mode={mode}
              version={version}
              applicationName={applicationName}
              metadataExportName={metadataExportName}
              retryInterval={retryInterval}
              maxRetries={maxRetries}
              availableVersions={connectionTest.availableVersions}
              createdSourceId={createdSourceId}
              hostname={hostname}
              username={username}
              password={password}
              syncLoading={syncMetadata.syncLoading}
              syncResult={syncMetadata.syncResult}
              configLoading={configLoading}
              skipLoading={skipLoading}
              loadedEpmConfig={loadedEpmConfig}
              isValid={isStep2Valid()}
              onVersionChange={onVersionChange}
              onApplicationNameChange={onApplicationNameChange}
              onMetadataExportNameChange={onMetadataExportNameChange}
              onRetryIntervalChange={onRetryIntervalChange}
              onMaxRetriesChange={onMaxRetriesChange}
              onSyncMetadata={() => syncMetadata.handleSyncMetadata(hostname, username, password, version, applicationName, metadataExportName, createdSourceId)}
              onSaveAndNext={handleSaveEPMConfigurationAndNext}
              onBack={handleBack}
              onSkip={handleSkip}
            />
          </StepContent>
        </Step>
        
        <Step>
          <StepLabel>Setup Cubes</StepLabel>
          <StepContent>
            <OracleEPMCubesStep
              cubes={cubeManagement.cubes}
              availableDimensions={syncMetadata.availableDimensions}
              loadedCubes={cubeManagement.loadedCubes}
              cubeSaveLoading={cubeManagement.cubeSaveLoading}
              cubeSaveResult={cubeManagement.cubeSaveResult}
              isValid={isStep3Valid()}
              onAddCube={cubeManagement.handleAddCube}
              onCubeNameChange={cubeManagement.handleCubeNameChange}
              onDimensionToggle={cubeManagement.handleDimensionToggle}
              onRemoveCube={cubeManagement.handleRemoveCube}
              onSaveCubes={handleSaveCubes}
              onBack={handleBack}
            />
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
};

